import * as B from '@blueprintjs/core';
import React from 'react';

import {usePushNotification} from 'app/components/Notification';

import {useGrid} from './GridContext';
import cs from './styles.module.scss';

// Progress thresholds that trigger a celebration. Order matters: detection
// walks the list and fires only the first newly-crossed milestone per render.
const MILESTONES = [0.25, 0.5, 0.75, 1.0] as const;
type Milestone = (typeof MILESTONES)[number];

const MILESTONE_MESSAGES: Record<Milestone, string> = {
  0.25: 'Great start — 25% reviewed!',
  0.5: 'Halfway there!',
  0.75: 'Almost done — 75% reviewed!',
  1.0: 'Property fully reviewed!',
};

// Confetti piece configs for the 100% burst: hardcoded positions, delays, durations,
// and colors drawn from the design palette. No randomness keeps renders deterministic.
const CONFETTI_PIECES: {left: string; delay: string; duration: string; color: string}[] = [
  {left: '5%', delay: '0s', duration: '2.0s', color: '#137CBD'},
  {left: '10%', delay: '0.15s', duration: '2.3s', color: '#F2D680'},
  {left: '18%', delay: '0.05s', duration: '1.9s', color: '#B57EEB'},
  {left: '25%', delay: '0.3s', duration: '2.1s', color: '#238551'},
  {left: '32%', delay: '0.1s', duration: '2.4s', color: '#FF9A00'},
  {left: '40%', delay: '0.25s', duration: '2.0s', color: '#D9593D'},
  {left: '47%', delay: '0s', duration: '1.8s', color: '#137CBD'},
  {left: '53%', delay: '0.2s', duration: '2.2s', color: '#F2D680'},
  {left: '60%', delay: '0.1s', duration: '2.0s', color: '#238551'},
  {left: '67%', delay: '0.35s', duration: '1.9s', color: '#B57EEB'},
  {left: '74%', delay: '0.05s', duration: '2.3s', color: '#FF9A00'},
  {left: '80%', delay: '0.15s', duration: '2.1s', color: '#D9593D'},
  {left: '87%', delay: '0.3s', duration: '2.0s', color: '#137CBD'},
  {left: '93%', delay: '0s', duration: '1.8s', color: '#F2D680'},
  {left: '15%', delay: '0.4s', duration: '2.4s', color: '#238551'},
  {left: '35%', delay: '0.45s', duration: '2.2s', color: '#B57EEB'},
  {left: '55%', delay: '0.5s', duration: '2.0s', color: '#FF9A00'},
  {left: '70%', delay: '0.4s', duration: '1.9s', color: '#D9593D'},
  {left: '85%', delay: '0.35s', duration: '2.3s', color: '#137CBD'},
  {left: '97%', delay: '0.2s', duration: '2.1s', color: '#F2D680'},
];

/**
 * Watches grid review progress and fires celebratory feedback at 25/50/75/100%.
 * Renders a viewport-wide gradient sweep (and confetti at 100%) that are fully
 * pointer-events: none so they never block map interaction.
 *
 * Must be mounted inside <GridProvider> and <PushNotificationProvider>.
 */
const GridDelightOverlay: React.FunctionComponent = () => {
  const {featureId, reviewedCellIds, existingCellIds} = useGrid();
  const pushNotification = usePushNotification();

  const [activeMilestone, setActiveMilestone] = React.useState<Milestone | null>(null);
  const firedMilestonesRef = React.useRef<Set<Milestone>>(new Set());

  // When the active property changes, reset milestone tracking and pre-populate any
  // milestones already met (so we don't re-celebrate saved progress on load).
  React.useEffect(() => {
    const total = existingCellIds?.size ?? 0;
    const progress = total > 0 ? reviewedCellIds.size / total : 0;
    firedMilestonesRef.current = new Set(MILESTONES.filter((m) => progress >= m));
  }, [featureId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect the first milestone crossing whenever reviewed count changes.
  React.useEffect(() => {
    const total = existingCellIds?.size ?? 0;
    if (total === 0) return;
    const progress = reviewedCellIds.size / total;

    const hit = MILESTONES.find((m) => progress >= m && !firedMilestonesRef.current.has(m));
    if (!hit) return;

    firedMilestonesRef.current.add(hit);

    pushNotification({
      message: MILESTONE_MESSAGES[hit],
      autoHideDuration: 3000,
      options: {intent: B.Intent.PRIMARY},
    });

    setActiveMilestone(hit);
    const timer = setTimeout(() => setActiveMilestone(null), 1800);
    return () => clearTimeout(timer);
  }, [reviewedCellIds.size, existingCellIds?.size]); // eslint-disable-line react-hooks/exhaustive-deps

  if (activeMilestone !== 1.0) return null;

  return (
    <>
      {/* Confetti burst at 100% completion */}
      {CONFETTI_PIECES.map((piece, i) => (
        <div
          key={i}
          className={cs.gridConfettiPiece}
          style={{
            left: piece.left,
            backgroundColor: piece.color,
            // CSS custom properties consumed by the gridConfettiFall animation
            ['--confetti-delay' as string]: piece.delay,
            ['--confetti-duration' as string]: piece.duration,
          }}
        />
      ))}
    </>
  );
};

export default GridDelightOverlay;
